// EF-179: cancel a Schwab orders identified by exact orderId — distinct from
// the EF-177 cancelOpenOrdersForSymbol helper which uses a symbol-only filter.
//
// Used by the Position Workspace setup-card cancel-and-clear path. The
// symbol/account heuristic in cancelOpenOrdersForSymbol is unsafe on this
// surface because the active position's stops live at the SAME symbol AND
// SAME accounts as the add-on setup. Only orderId equality is precise enough
// to guarantee we never touch the active position's protection.
//
// EF-193: split into resolveCancelableTrackedOrders + executeCancelPayloads
// so Delete can resolve + execute without double-fetching /api/schwab/orders
// (the composed helper made two GETs when both halves were called in
// sequence). The composed export `cancelOrdersByTrackedIds` is preserved
// verbatim — every existing call site keeps working.

const TERMINAL_STATUSES = ['FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'REPLACED']
const CANCELED_STATUSES = new Set(['CANCELED', 'CANCELLED'])
const DEFAULT_CONFIRMATION_MAX_ATTEMPTS = 4
const DEFAULT_CONFIRMATION_RETRY_DELAY_MS = 1000
const DEFAULT_CONFIRMATION_TIMEOUT_MS = 5000

function sleep(ms) {
  return ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve()
}

function trackedOrderRef(tracked) {
  const accountNumber = String(tracked?.accountNumber ?? tracked?.account_number ?? '').trim()
  const orderId = String(tracked?.orderId ?? tracked?.order_id ?? '').trim()
  return { accountNumber, orderId }
}

function orderKey(accountNumber, orderId) {
  return `${String(accountNumber ?? '').trim()}|${String(orderId ?? '').trim()}`
}

function cancellationSnapshot(trackedOrders, brokerOrders, { requireUnfilled = false } = {}) {
  const brokerByKey = new Map(
    (Array.isArray(brokerOrders) ? brokerOrders : [])
      .map(order => {
        const accountNumber = order?.accountNumber ?? order?.account_number
        const orderId = order?.orderId ?? order?.order_id
        return [orderKey(accountNumber, orderId), order]
      })
      .filter(([key]) => key !== '|')
  )

  const details = trackedOrders.map(tracked => {
    const { accountNumber, orderId } = trackedOrderRef(tracked)
    const brokerOrder = brokerByKey.get(orderKey(accountNumber, orderId))
    const status = brokerOrder
      ? String(brokerOrder.status || '').trim().toUpperCase() || 'UNKNOWN'
      : 'NOT_FOUND'
    return {
      accountNumber,
      orderId,
      status,
      confirmed: CANCELED_STATUSES.has(status)
        && Number(brokerOrder?.filledQuantity ?? 0) === 0
        && (!requireUnfilled || brokerOrder?.filledQuantity != null),
    }
  })
  const remainingTrackedOrders = trackedOrders.filter((_tracked, index) => !details[index].confirmed)
  return {
    confirmed: trackedOrders.length > 0 && remainingTrackedOrders.length === 0,
    confirmedCount: trackedOrders.length - remainingTrackedOrders.length,
    remainingTrackedOrders,
    details,
    fetchError: null,
  }
}

/**
 * Fetch live Schwab orders and resolve the subset of tracked IDs that are
 * still non-terminal at the broker. Returns cancel-ready payloads (using
 * `existingOrderId` per oms-conventions.md) or a populated `fetchError` if
 * /api/schwab/orders failed. Never throws.
 */
export async function resolveCancelableTrackedOrders(trackedOrders, session, { timeoutMs = 0, entryOnly = false, expectedSymbol = null } = {}) {
  if (!Array.isArray(trackedOrders) || trackedOrders.length === 0) {
    return { cancelPayloads: [], fetchError: null }
  }

  const auth = `Bearer ${session?.access_token || ''}`
  const trackedRefs = trackedOrders
    .map((tracked) => {
      const accountNumber = String(tracked?.accountNumber ?? tracked?.account_number ?? '').trim()
      const orderId = String(tracked?.orderId ?? tracked?.order_id ?? '').trim()
      return accountNumber && orderId ? `${accountNumber}:${orderId}` : null
    })
    .filter(Boolean)
  const ordersUrl = trackedRefs.length > 0
    ? `/api/schwab/orders?${new URLSearchParams({ orderRefs: trackedRefs.join(',') }).toString()}`
    : '/api/schwab/orders'

  let allOrders
  const boundedTimeoutMs = Number.isFinite(Number(timeoutMs)) && Number(timeoutMs) > 0
    ? Number(timeoutMs)
    : 0
  const controller = boundedTimeoutMs > 0 ? new AbortController() : null
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), boundedTimeoutMs)
    : null
  try {
    const res = await fetch(ordersUrl, {
      headers: { Authorization: auth },
      ...(controller ? { signal: controller.signal } : {}),
    })
    if (!res.ok) {
      return {
        cancelPayloads: [],
        fetchError: { message: `Failed to fetch Schwab orders: HTTP ${res.status}` },
      }
    }
    const data = await res.json()
    allOrders = Array.isArray(data?.orders) ? data.orders : []
  } catch (err) {
    return {
      cancelPayloads: [],
      fetchError: {
        message: err?.name === 'AbortError' && boundedTimeoutMs > 0
          ? `Schwab order refresh timed out after ${boundedTimeoutMs}ms`
          : `Failed to fetch Schwab orders: ${err?.message || String(err)}`,
      },
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }

  // Schwab order identity is account + order ID, including when IDs repeat.
  const liveById = new Map()
  for (const order of allOrders) {
    const ref = trackedOrderRef(order)
    liveById.set(orderKey(ref.accountNumber, ref.orderId), order)
  }

  // Filter the tracked set to orders that still exist at Schwab in a
  // non-terminal state. Anything missing or terminal is silently dropped
  // (already gone, not a failure).
  const cancelPayloads = []
  for (const t of trackedOrders) {
    const ref = trackedOrderRef(t)
    const live = liveById.get(orderKey(ref.accountNumber, ref.orderId))
    if (!live) continue
    const status = String(live.status || '').toUpperCase()
    if (TERMINAL_STATUSES.includes(status)) continue
    const leg = live?.orderLegCollection?.[0]
    const sym = leg?.instrument?.symbol
    if (entryOnly && (
      !['BUY', 'BUY_TO_OPEN', 'SELL_SHORT', 'SELL_TO_OPEN'].includes(String(leg?.instruction || '').toUpperCase())
      || live.filledQuantity == null || Number(live.filledQuantity) !== 0
      || (expectedSymbol && String(sym || '').toUpperCase() !== String(expectedSymbol).toUpperCase())
    )) {
      return { cancelPayloads: [], fetchError: { message: 'Tracked order is not a verified unfilled entry for this setup. Reconcile it before cancellation.' } }
    }
    cancelPayloads.push({
      account_number: ref.accountNumber,
      symbol: String(sym || '').toUpperCase(),
      existingOrderId: t.orderId ?? t.order_id,
    })
  }

  return { cancelPayloads, fetchError: null }
}

/**
 * POST resolved cancel payloads to /api/oms/execute and split the per-order
 * results into success/failure counts. Empty input returns zero-result
 * without making a network call. Never throws.
 */
export async function executeCancelPayloads(cancelPayloads, session, { brokerWriteProof = null } = {}) {
  if (!Array.isArray(cancelPayloads) || cancelPayloads.length === 0) {
    return { attempted: 0, successCount: 0, failureCount: 0, errors: [] }
  }

  const auth = `Bearer ${session?.access_token || ''}`
  const requestBody = brokerWriteProof
    ? { action: 'CANCEL', orders: cancelPayloads, brokerWriteProof }
    : { action: 'CANCEL', orders: cancelPayloads }

  let omsBody
  try {
    const res = await fetch('/api/oms/execute', {
      method: 'POST',
      headers: { Authorization: auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
    omsBody = res.ok ? await res.json() : await res.json().catch(() => ({}))
    if (!res.ok && !Array.isArray(omsBody?.results)) {
      return {
        attempted: cancelPayloads.length,
        successCount: 0,
        failureCount: cancelPayloads.length,
        errors: cancelPayloads.map(o => ({
          accountNumber: o.account_number,
          orderId: o.existingOrderId,
          message: omsBody?.error || `OMS returned HTTP ${res.status}`,
        })),
      }
    }
  } catch (err) {
    return {
      attempted: cancelPayloads.length,
      successCount: 0,
      failureCount: cancelPayloads.length,
      errors: cancelPayloads.map(o => ({
        accountNumber: o.account_number,
        orderId: o.existingOrderId,
        message: `OMS request failed: ${err?.message || String(err)}`,
      })),
    }
  }

  const results = Array.isArray(omsBody?.results) ? omsBody.results : []
  let successCount = 0
  let failureCount = 0
  const errors = []
  for (let i = 0; i < cancelPayloads.length; i++) {
    const r = results[i]
    if (r?.success === true) {
      successCount++
    } else {
      failureCount++
      errors.push({
        accountNumber: r?.accountNumber ?? cancelPayloads[i].account_number,
        orderId: r?.orderId ?? cancelPayloads[i].existingOrderId,
        message: r?.error || r?.message || 'Cancel failed',
      })
    }
  }

  return { attempted: cancelPayloads.length, successCount, failureCount, errors }
}

/**
 * Confirm cancellation against Schwab's exact per-account order resources.
 * A successful DELETE is only an acknowledgement; metadata may be cleared
 * only after every tracked parent is returned with CANCELED/CANCELLED status.
 * Missing orders, unknown statuses, timeouts, and read failures remain
 * unconfirmed so the setup stays visible for operator review.
 */
export async function confirmCanceledTrackedOrders(trackedOrders, session, options = {}) {
  if (!Array.isArray(trackedOrders) || trackedOrders.length === 0) {
    return {
      confirmed: true,
      confirmedCount: 0,
      remainingTrackedOrders: [],
      details: [],
      fetchError: null,
    }
  }

  const refs = trackedOrders
    .map(trackedOrderRef)
    .filter(ref => ref.accountNumber && ref.orderId)
    .map(ref => `${ref.accountNumber}:${ref.orderId}`)
  if (refs.length !== trackedOrders.length) {
    return {
      ...cancellationSnapshot(trackedOrders, []),
      fetchError: { message: 'One or more tracked Schwab orders is missing an account number or order ID' },
    }
  }

  const maxAttempts = Math.max(1, Number(options.maxAttempts) || DEFAULT_CONFIRMATION_MAX_ATTEMPTS)
  const requestedRetryDelayMs = Number(options.retryDelayMs)
  const retryDelayMs = Number.isFinite(requestedRetryDelayMs) && requestedRetryDelayMs >= 0
    ? requestedRetryDelayMs
    : DEFAULT_CONFIRMATION_RETRY_DELAY_MS
  const timeoutMs = Math.max(1, Number(options.timeoutMs) || DEFAULT_CONFIRMATION_TIMEOUT_MS)
  const params = new URLSearchParams({
    orderRefs: refs.join(','),
    includeTerminal: '1',
  })
  const ordersUrl = `/api/schwab/orders?${params.toString()}`
  const auth = `Bearer ${session?.access_token || ''}`
  let latest = cancellationSnapshot(trackedOrders, [])
  let lastError = null

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    try {
      const res = await fetch(ordersUrl, {
        headers: { Authorization: auth },
        signal: controller.signal,
      })
      if (!res.ok) throw new Error(`Schwab confirmation returned HTTP ${res.status}`)
      const data = await res.json()
      latest = cancellationSnapshot(trackedOrders, data?.orders, options)
      lastError = null
      if (latest.confirmed) return latest
    } catch (err) {
      lastError = {
        message: err?.name === 'AbortError'
          ? `Schwab cancellation confirmation timed out after ${timeoutMs}ms`
          : `Could not confirm Schwab cancellations: ${err?.message || String(err)}`,
      }
    } finally {
      clearTimeout(timeoutId)
    }

    if (attempt < maxAttempts) await sleep(retryDelayMs)
  }

  return { ...latest, fetchError: lastError }
}

/**
 * Composed entry-point. Public signature and return shape preserved from
 * the pre-EF-193 helper so every existing call site keeps working.
 */
export async function cancelOrdersByTrackedIds(trackedOrders, session, options = {}) {
  if (!Array.isArray(trackedOrders) || trackedOrders.length === 0) {
    return { attempted: 0, successCount: 0, failureCount: 0, errors: [] }
  }

  const { cancelPayloads, fetchError } = await resolveCancelableTrackedOrders(trackedOrders, session)
  if (fetchError) {
    return {
      attempted: 0,
      successCount: 0,
      failureCount: 0,
      errors: [{ accountNumber: null, orderId: null, message: fetchError.message }],
    }
  }
  return executeCancelPayloads(cancelPayloads, session, options)
}
